import { MouseEvent as ReactMouseEvent, useEffect, useRef, useState } from "react";

import { downloadFile, hasValue, KeyedArray, log, SortTypeEnum } from "@repo/helpers";

import GridMarkup from "./markup";
import style from "./style.module.scss";
import { ColumnConfiguration, GridBaseProps, GridSaveState, IFilter, SortBy } from "./types";

export function Grid<T>(props: GridBaseProps<T>) {
	const { resizeable = false } = props;
	const isInitialMount = useRef(true);
	const currentResizeTh = useRef<HTMLTableCellElement>(null);
	const offset = useRef(0);
	const tableWidth = useRef(0);
	const tableRef = useRef<HTMLTableElement>(null);
	const columnWidth = useRef(0);
	const columnIndex = useRef(-1);
	const minWidth = useRef(0);

	const [columns, setColumns] = useState(props.columns);
	const [currentlyResizing, setCurrentlyResizing] = useState(false);
	const [pageSize, setPageSize] = useState(props.pageSize);
	const [exporting, setExporting] = useState(false);

	useEffect(() => {
		if (props.stateful) {
			loadGridConfiguration();
		}
		setColumns(columns);

		isInitialMount.current = false;
		return removeEvents;
	}, []);

	useEffect(() => {
		if (resizeable) {
			document.addEventListener("mousemove", resizeColumn);
			document.addEventListener("mouseup", clearCurrentResizeTh);
			if (tableRef.current) {
				// initialize table width
				tableWidth.current = tableRef.current.offsetWidth;
				const pageColumns = tableRef.current.querySelectorAll<HTMLTableHeaderCellElement>(
					"." + style.resizeableHeader,
				);
				// set the default width for each column to prevent table from resizing columns on resize
				for (let i = 0; i < pageColumns.length; i++) {
					const currentWidth = pageColumns[i].offsetWidth;
					const width = Math.max(isNaN(currentWidth) ? 0 : currentWidth, columns[i].minWidth);
					columns[i].width = width + "px";
					pageColumns[i].style.width = width + "px";
				}
				setColumns([...columns]);
			}
		} else {
			removeEvents();
		}
	}, [resizeable]);

	useEffect(() => {
		if (isInitialMount.current) return;
		if (props.setItemsPerPage) props.setItemsPerPage(pageSize);
		else log(`${props.id} Grid has not been set up for custom items per page.`);
	}, [pageSize]);

	useEffect(() => {
		if (exporting) excelExport();
	}, [exporting]);

	useEffect(() => {
		setColumns(props.columns);
	}, [props.columns]);

	useEffect(() => {
		if (!props.loading) {
			const pageColumns = tableRef.current.querySelectorAll<HTMLTableCellElement>("." + style.resizeableHeader);

			for (let i = 0; i < pageColumns.length; i++) {
				const width = Math.max(pageColumns[i].offsetWidth || 0, columns[i].minWidth || 0);
				columns[i].width = width + "px";
				pageColumns[i].style.width = width + "px";
			}
		}
	}, [props.loading]);

	function removeEvents() {
		document.removeEventListener("mousemove", resizeColumn);
		document.removeEventListener("mouseup", clearCurrentResizeTh);
	}

	/**
	 * Use the function passed in props to call the api, get any grid saved state
	 * and send it to the gridConfigurationLoaded function
	 */
	function loadGridConfiguration() {
		if (props.loadGridState) {
			props.loadGridState(gridConfigurationLoaded);
		}
	}

	/**
	 * Callback when grid configuration is loaded from the api
	 * @param configuration
	 */
	function gridConfigurationLoaded(configuration: GridSaveState) {
		const stateColumns = columns.map((c) => c);
		// if the configuration is blank (no saved state)
		if (!hasValue(configuration?.columns)) return;
		let sortBySet = null;
		// restore the configuration for each column
		configuration.columns.forEach((column, index) => {
			// if the column was selected for sorting, store it so the grid can be refreshed with the correct
			// sorting chosen
			if ((column.sortBy ?? SortTypeEnum.none) !== SortTypeEnum.none) {
				sortBySet = {
					order: column.sortBy,
					name: column.propertyName || column.name,
				} as SortBy;
			}
			// if the name of the column matches up with the column index, then just update it's configuration
			if (column.name === stateColumns[index].name) {
				stateColumns[index].visible = column.visible ?? true;
				stateColumns[index].width = column.width ?? "";
				stateColumns[index].sortBy = column.sortBy ?? SortTypeEnum.none;
			}
			// else look for the column by name and update it's configuration
			else {
				const matchingColumns = stateColumns.filter((c) => c.name === column.name);
				for (const matchingColumn of matchingColumns) {
					matchingColumn.visible = column.visible ?? true;
					matchingColumn.width = column.width ?? "";
					matchingColumn.sortBy = column.sortBy ?? SortTypeEnum.none;
				}
			}
		});
		setColumns(stateColumns);
		// have to adjust the table width since we adjusted the column widths
		if (hasValue(tableRef.current)) {
			tableWidth.current = columns
				.map((column) => parseFloat(column.width.replace(/[^0-9.]/, "")))
				.reduce((total, width) => total + width);
			tableRef.current.style.width = tableWidth.current + "px";
		}
		// if the grid was saved while it was sorted, refresh the grid with the selected sort option
		if (hasValue(sortBySet)) {
			props.getData(1, { sortBy: sortBySet } as IFilter);
		}
	}

	/**
	 * Serialize the grid configuration and save it to the API through the saveGridState property
	 */
	function saveGridConfiguration() {
		if (tableRef.current && props.stateful && props.saveGridState) {
			props.saveGridState(
				JSON.stringify({
					columns: generateColumnConfiguration(),
					pageSize: props.pageSize,
				}),
			);
		}
	}

	/**
	 * Pass an empty string for the grid configuration to the saveGridState prop in order to clear the saved config
	 */
	function clearGridConfiguration() {
		if (props.stateful && props.saveGridState) {
			props.saveGridState("");
		}
	}

	async function excelExport() {
		const { exportable, getRecordsToExport, columns, gridNameForExport, apiRoute } = props;
		if (!exportable || !getRecordsToExport) return;
		try {
			const rows = await getRecordsToExport();
			if (rows.totalItems <= 0) {
				alert("No data to export");
				setExporting(false);
				return;
			}

			const displayValues = [];
			const visibleColumns = columns.filter((c) => c.visible);
			const columnsToKeep = new KeyedArray(visibleColumns, (k) => k.name);
			for (const rowItem of rows.items) {
				const keptColumns = visibleColumns.filter((c) => columnsToKeep.has(c.name));
				const items = keptColumns.map((c) => {
					const { name } = c;
					const value = c.getValue(rowItem);
					return { name, value };
				});
				const values = items
					.map((item) => {
						const { name, value } = item;
						if (!hasValue(value)) return "";
						if (typeof value === "object" && Object.keys(value).length > 0) columnsToKeep.removeId(name);
						if (!columnsToKeep.has(name)) return null;
						return value;
					})
					.filter(hasValue);
				displayValues.push(values);
			}

			const xhr = new XMLHttpRequest();
			xhr.responseType = "blob";
			xhr.onload = () =>
				generateExcelFileDownload(
					xhr.status,
					xhr.response,
					xhr.getResponseHeader("content-disposition"),
					gridNameForExport,
				);
			xhr.onerror = () => {
				alert("Unable to generate excel file");
				setExporting(false);
			};
			xhr.open("POST", `${apiRoute || ""}/GenerateExcelFile`);
			const formData = new FormData();
			formData.append("rows", JSON.stringify(displayValues));
			formData.append("headers", JSON.stringify(columnsToKeep.map((c) => c.name)));
			xhr.send(formData);
		} catch (e) {
			log(e, "error");
			alert("Unable to create export");
			setExporting(false);
		}
	}

	function excelExportClicked() {
		setExporting(true);
	}

	function generateExcelFileDownload(status: number, response: any, disposition: string, gridNameForExport: string) {
		if (status === 200) {
			if (response == null) {
				alert("Unable to generate excel file");
				return;
			}
			const matches = /"([^"]*)"/.exec(disposition);
			const blob = new Blob([response], {
				type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet ",
			});

			const url = window.URL.createObjectURL(blob);
			const fileName =
				matches != null && matches[1] && gridNameForExport
					? `${gridNameForExport} ${matches[1]}`
					: "export.xlsx";
			downloadFile(url, fileName);
			window.URL.revokeObjectURL(url);
		} else {
			alert("Unable to generate excel file");
		}
		setExporting(false);
	}

	function generateColumnConfiguration() {
		return columns.map((column) => {
			return {
				name: column.name,
				propertyName: column.propertyName,
				visible: column.visible,
				width: column.width,
				sortBy: column.sortBy,
				clientTemplate: column.clientTemplate,
			} as ColumnConfiguration;
		});
	}

	/**
	 * Set the sort value for the selected column
	 * @param columnIndex Index of the column
	 */
	function sortOnColumn(columnIndex: number) {
		// if not sortable or if being resized, ignore the click event
		if (!props.sortable || currentResizeTh.current) return;
		// currentlyResizing is set on mousedown of a grid table head element
		// on mouse up is also seen as clicking on a table head element
		// Since mouseup is triggered before onclick, letting go of the
		// resize event is seen as clicking on the column as well.
		// Therefore, if the mouse up event clears the currentResizeTh
		// before the sort on column function, this flag will catch
		// that and prevent the sort on column
		if (currentlyResizing) {
			setCurrentlyResizing(false);
			return;
		}

		const column = columns[columnIndex];
		const originalSort = column.sortBy;

		//So we don't do an unnecessary data load
		if (!column.sortable) return;

		columns.forEach((c) => (c.sortBy = SortTypeEnum.none));
		if (originalSort === SortTypeEnum.none) {
			column.sortBy = SortTypeEnum.ascending;
		} else if (originalSort === SortTypeEnum.ascending) {
			column.sortBy = SortTypeEnum.descending;
		}
		setColumns(columns);
		props.getData(1, {
			sortBy: {
				order: column.sortBy,
				name: column.propertyName || column.name,
			} as SortBy,
		} as IFilter);
	}

	/**
	 * Keep a reference of what column is currently being resized so that if the cursor goes over
	 * another column, it doesn't resize another column
	 * @param event
	 */
	function setCurrentResizeTh(event: ReactMouseEvent<HTMLDivElement>) {
		currentResizeTh.current = event.currentTarget.parentElement as HTMLTableHeaderCellElement;
		columnWidth.current = currentResizeTh.current.offsetWidth;
		offset.current = currentResizeTh.current.offsetWidth - event.pageX;
		columnIndex.current = parseInt(event.currentTarget.getAttribute("data-index"));
		if (currentResizeTh.current.style.minWidth === "") {
			minWidth.current = 0;
		} else {
			minWidth.current = parseFloat(currentResizeTh.current.style.minWidth.replace(/[^0-9.]/, ""));
		}
		setCurrentlyResizing(true);
	}

	/**
	 * Clear the reference of the column being resized
	 */
	function clearCurrentResizeTh() {
		if (hasValue(currentResizeTh.current)) {
			currentResizeTh.current = null;
			columns[columnIndex.current].width = columnWidth.current + "px";
			setColumns(columns);
		}
	}

	/**
	 * Mouse move over the table event. Will only resize when a TH resize element has been clicked and dragged
	 * @param event
	 */
	function resizeColumn(event: MouseEvent) {
		// a th element is being resized
		if (currentResizeTh.current) {
			// get the size change for the column. If the adjust value is less
			// than 75 px, keep the column to 75 px to prevent the column from
			// being collapsed
			const adjust = Math.max(10, minWidth.current, offset.current + event.pageX);
			// if the table ref if active (it should always be but just in case)
			// then adjust the table size to accommodate the change in column size
			if (hasValue(tableRef.current)) {
				tableWidth.current = tableWidth.current - columnWidth.current + adjust;
				tableRef.current.style.width = tableWidth.current + "px";
			}
			// set the new column size
			columnWidth.current = adjust;
			currentResizeTh.current.style.width = columnWidth.current + "px";
		}
	}

	function updateColumnVisibility(index: number, visibility: boolean) {
		columns[index].visible = visibility;
		setColumns([...columns]);
	}

	function refresh() {
		if (props.refresh) props.refresh();
		else props.getData(props.currentPage);
	}

	return (
		<GridMarkup<T>
			{...props}
			pageSize={pageSize}
			columns={columns}
			sortEventHandler={sortOnColumn}
			refresh={refresh}
			updateColumnVisibility={updateColumnVisibility}
			tableRef={tableRef}
			excelExportEvent={excelExportClicked}
			setItemsPerPage={setPageSize}
			saveGridConfiguration={saveGridConfiguration}
			clearGridConfiguration={clearGridConfiguration}
			setCurrentResizeTh={setCurrentResizeTh}
			exportDisabled={exporting}
		/>
	);
}

export default Grid;
export * from "./types";
export * from "./models";
